import type { ReactNode } from 'react'

import type { TFunction } from 'i18next'

import type { MasterDataTableColumn } from '~/components/common/MasterDataTable'

/**
 * 種別ごとに変わるデータ列の見出しと**揃え**。
 *
 * 見出しだけを `string[]` で受け取ると、 どの位置が enum 列 (エリア・売買) で
 * どの位置が数値列 (価格・数量) なのかを表側から判別できず、 `MasterDataTable` の
 * 既定 (= 左寄せ) に落ちる。 8080 の列揃え規約は「固定値列 (enum / 時刻 / フラグ) は
 * TH と揃えて center、 数値は right、 可変テキストは left」 (`CLAUDE.md:332` /
 * `css/components.css:830`) なので、 **見出しと一緒に揃えを渡す**。
 */
export type BidCsvPreviewDataColumn = {
  label: string
  /** 未指定なら可変テキストとして左寄せ。 */
  align?: 'left' | 'center' | 'right'
  /** 数値列。 等幅数字 + 右寄せになる。 */
  format?: 'number'
}

type BidCsvPreviewColumnsSpec<TRow> = {
  /** 種別ごとの表示列 (CSV の列順)。 */
  dataColumns: BidCsvPreviewDataColumn[]
  /** 行ステータスのバッジ (ステータスの区分は種別ごとに違う)。 */
  renderStatus: (row: TRow) => ReactNode
  /** `dataColumns[index]` の位置のセル。 */
  renderDataCell: (row: TRow, index: number) => ReactNode
  /** エラー内容のセル。 */
  renderMessage: (row: TRow) => ReactNode
  /**
   * 最後の列の見出し (i18n key)。 未指定なら「エラー内容」。 エラー以外の注意 (既存の札との重複の警告など) も
   * 同じ列に出す画面は「備考」にする (`admin/data/uploads/common.md`「備考」列)。
   */
  messageHeaderKey?: string
}

/**
 * 入札 CSV 取込の差分プレビュー表 (Step2) の列の骨格。 「ステータス + 行番号 + 種別ごとの
 * 表示列 + エラー内容」 という並びはスポット通常入札・ブロック入札・時間前入札で同じなので、
 * ここに 1 つだけ置く。 行ステータスの区分・表示列の中身・エラー内容の出し方は種別ごとに
 * 違うので、 呼び出し側が渡す。
 *
 * component 本体の外で列定義を組むための関数 (cell が「render のたびに新しい
 * コンポーネント型」にならないようにする。 react/no-unstable-nested-components)。
 */
export function buildBidCsvPreviewColumns<TRow extends { rowNumber: number }>(
  t: TFunction,
  {
    dataColumns,
    renderStatus,
    renderDataCell,
    renderMessage,
    messageHeaderKey = 'page:biddingCommon.csvPreview.columns.message',
  }: BidCsvPreviewColumnsSpec<TRow>,
): Array<MasterDataTableColumn<TRow>> {
  return [
    // ステータスは全画面共通で列の先頭に置く (2026-08-27 デザイナー判断。
    // design `bidding/spot-csv-import.html` / `bidding/intraday-csv-import.html`)。
    {
      key: 'status',
      header: t('page:biddingCommon.csvPreview.columns.status'),
      cell: renderStatus,
    },
    {
      key: 'rowNumber',
      header: t('page:biddingCommon.csvPreview.columns.rowNumber'),
      align: 'right',
      cell: (row) => row.rowNumber,
    },
    ...dataColumns.map((col, index) => ({
      key: `data-${index}`,
      header: col.label,
      align: col.align,
      format: col.format,
      cell: (row: TRow) => renderDataCell(row, index),
    })),
    {
      key: 'message',
      header: t(messageHeaderKey),
      cell: renderMessage,
    },
  ]
}
